import { ReactNode, useEffect, useState } from "react";
import { ModalControls } from "../../../../customHooks/useModalControl";
import {
  formatDate,
  formatToCurrency,
  formatToNumber,
} from "../../../../helperFunctions";
import { useOfficialFetchLoanDetailsQuery } from "../../../../redux/features/loans/loans.api";
import { FullLoanDetails } from "../../../../redux/features/loans/loans.type";
import {
  loanrepaymentMethodMap,
  loanStatusMap,
} from "../../../../utils/constants";
import { Button } from "../../../buttons/button";
import { TagCard } from "../../../cards/tagCard";
import { MoreDetailsModal } from "../moreDetailsModal";
import { LoanRepaymentScheduleModal } from "./repaymentScheduleModal";

export const LoanDetailsModal = ({
  loanId,
  cooperativeId,
  modalControls,
  onClose,
}: {
  loanId: number;
  cooperativeId: number;
  modalControls: ModalControls;
  onClose: () => void;
}) => {
  const [fullLoanDetails, setFullLoanDetails] = useState<FullLoanDetails>();
  const [loanDetails, setLoanDetails] = useState<
    {
      title: ReactNode;
      element: ReactNode;
    }[]
  >([]);
  const [guarantorDetails, setGuarantorDetails] = useState<
    {
      title: ReactNode;
      element: ReactNode;
    }[]
  >([]);

  const { modal, modalOpen, handleModalClose, handleModalOpen } = modalControls;

  const {
    data: officialFullLoanDetails,
    isFetching: officialFetchingLoanDetails,
  } = useOfficialFetchLoanDetailsQuery(
    {
      loan_id: loanId,
      detail: true,
      coop: cooperativeId!,
    },
    {
      skip: !loanId || !cooperativeId || !modalOpen,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    setFullLoanDetails(officialFullLoanDetails?.data);
  }, [officialFullLoanDetails]);

  useEffect(() => {
    setLoanDetails([
      {
        title: "First Name",
        element: fullLoanDetails?.MemberID?.User?.FirstName,
      },
      {
        title: "Middle Name",
        element: fullLoanDetails?.MemberID?.User?.MiddleName,
      },
      {
        title: "Last Name",
        element: fullLoanDetails?.MemberID?.User?.LastName,
      },
      {
        title: "Phone Number",
        element: `${fullLoanDetails?.MemberID?.User?.PhoneNumberCode}${fullLoanDetails?.MemberID?.User?.PhoneNumber}`,
      },
      {
        title: "Loan Type",
        element: fullLoanDetails?.LoanProduct?.LoanName,
      },
      {
        title: "Loan Amount",
        element: formatToCurrency(fullLoanDetails?.Amount ?? 0, {
          currency: "NGN",
          decimalPlaces: 2,
        }),
      },
      {
        title: "Loan Tenor:",
        element: `${formatToNumber(fullLoanDetails?.Tenure ?? 0, {})} months`,
      },
      {
        title: "Interest Rate",
        element: `${formatToNumber(
          fullLoanDetails?.LoanProduct?.MaxInterestRate ?? 0,
          {
            style: "percent",
          }
        )}`,
      },
      {
        title: "Repayment Method",
        element: fullLoanDetails?.RepaymentMode
          ? `${
              loanrepaymentMethodMap[fullLoanDetails?.RepaymentMode]?.shortText
            }` +
            (["card", "bank"].includes(fullLoanDetails.RepaymentMode) &&
            fullLoanDetails?.RepaymentMethodID?.last4
              ? ` ∙ ****${
                  fullLoanDetails.RepaymentMode === "card"
                    ? fullLoanDetails?.RepaymentMethodID?.last4
                    : fullLoanDetails?.RepaymentMethodID?.AccountNumber
                } (${
                  fullLoanDetails.RepaymentMode === "card"
                    ? fullLoanDetails?.RepaymentMethodID?.card_type
                    : fullLoanDetails?.RepaymentMethodID?.BankName
                })`
              : "")
          : "N/A",
      },
      {
        title: "Status",
        element: (
          <TagCard
            text={loanStatusMap[fullLoanDetails?.Status ?? "in-progress"].text}
            type={
              loanStatusMap[fullLoanDetails?.Status ?? "in-progress"].tagVariant
            }
          />
        ),
      },
      {
        title: "Date Requested",
        element: formatDate(fullLoanDetails?.DateAdded),
      },
      {
        title: "Repayment Schedule",
        element: (
          <Button
            className="!py-0 !px-0"
            type="button"
            onClick={() => {
              handleModalOpen("viewRepaymentScheduleModal");
            }}
            text="View Schedule"
            variation="plain"
          />
        ),
      },
    ]);

    setGuarantorDetails([
      {
        title: "Number of Guarantors",
        element: formatToNumber(fullLoanDetails?.NoOfGuarantor ?? 0, {}),
      },
      ...(fullLoanDetails?.Guarantor ?? []).flatMap((guarantor, idx) => {
        return [
          {
            title: `Guarantor ${idx + 1}`,
            element: `${guarantor?.GuarantorMemberID?.User?.FirstName} ${guarantor?.GuarantorMemberID?.User?.LastName}`,
          },
          {
            title: "Amount Guaranteed",
            element: formatToCurrency(guarantor?.AmountGuarantee ?? 0, {
              currency: "NGN",
              decimalPlaces: 2,
            }),
          },
        ];
      }),
    ]);
    // eslint-disable-next-line
  }, [fullLoanDetails]);

  return (
    <>
      <MoreDetailsModal
        modalOpen={modalOpen && modal === "loanDetailsModal"}
        onModalClose={() => {
          handleModalClose();
          onClose();
        }}
        title="Loan Details"
        mainData={{
          ...Object.fromEntries(
            loanDetails?.map((item) => {
              return [item.title, item.element];
            }) ?? []
          ),
        }}
        extraInfoTitle="Guarantors Information"
        secondaryData={{
          ...Object.fromEntries(
            guarantorDetails?.map((item) => {
              return [item.title, item.element];
            }) ?? []
          ),
        }}
        loading={officialFetchingLoanDetails}
      />

      {fullLoanDetails ? (
        <>
          <LoanRepaymentScheduleModal
            isModalOpen={modalOpen && modal === "viewRepaymentScheduleModal"}
            onModalClose={() => {
              handleModalClose();
            }}
            loanDetails={fullLoanDetails}
            type={"official"}
          />
        </>
      ) : undefined}
    </>
  );
};
